import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import inventoryitemService from '../../../../services/inventoryitemService';
import {
  ArrowLeft,
  History,
  Loader2,
  RefreshCw,
  Boxes,
  AlertTriangle,
  PackageX,
  CheckCircle2,
  Filter,
  ChevronDown,
} from 'lucide-react';

/**
 * Inventory Item History (component)
 * Rendered by src/pages/Inventory/InventoryItemHistoryPage.jsx inside ModuleLayout.
 * Route: /inventory/items/:id/history
 *
 * Replaces the old "Item History" modal on the Inventory Items page.
 * Layout (top → bottom):
 *   1. Header card   — item summary (old popup content, promoted to a card)
 *   2. Filters card  — Period presets + custom Date Range with Apply
 *   3. Table card    — Stock Movement History
 *
 * DATA SOURCES (two independent APIs):
 *   • Item summary   → GET /api/inventory/items/summary (existing endpoint,
 *     returns ALL items in snake_case). The row is resolved by numeric id
 *     from the route (:id) — never by name matching.
 *   • Movements      → GET /api/inventory/item-history with itemId, period,
 *     startDate, endDate, type, offset, limit.
 *     Response: { total, movements: [{ id, updateType, quantity,
 *     stockBefore, stockAfter, unitCost, supplierName, billId, billNumber,
 *     purchaseOrderId, poNumber, reason, notes, createdAt, createdBy }] }
 *     Reference: purchase → poNumber (via stock_updates.id_purchase_order),
 *     consumption → billNumber (via stock_updates.id_bill), wastage → none.
 *   The summary loads once per :id; the table reloads whenever filters or
 *   pagination change. The two never block each other.
 */

// ─── Period filter (same enum + behaviour as Stock Status / Consumption) ─────
const PERIOD_OPTIONS = [
  { key: 'today',     label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'thisWeek',  label: 'This Week' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
];

const PERIOD_ENUM = {
  today:     'TODAY',
  yesterday: 'YESTERDAY',
  thisWeek:  'THIS_WEEK',
  thisMonth: 'THIS_MONTH',
  lastMonth: 'LAST_MONTH',
};

// Movement Type filter options. Values are the RAW backend types (the UI
// stores 'consumption', never 'sale') so the API param needs no re-mapping;
// only the label says "Sale". 'all' means no type param is sent.
const TYPE_OPTIONS = [
  { value: 'all',         label: 'All Types' },
  { value: 'purchase',    label: 'Purchase' },
  { value: 'consumption', label: 'Sale' },
  { value: 'wastage',     label: 'Wastage' },
];

// Backend 'consumption' is displayed as 'Sale' — display concern only,
// the API keeps returning the raw type.
const TYPE_CONFIG = {
  purchase:    { label: 'Purchase', badge: 'bg-green-100 text-green-700',   qty: 'text-emerald-600', sign: '+' },
  consumption: { label: 'Sale',     badge: 'bg-indigo-100 text-indigo-700', qty: 'text-indigo-600',  sign: '−' },
  wastage:     { label: 'Wastage',  badge: 'bg-red-100 text-red-700',       qty: 'text-red-600',     sign: '−' },
};

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getPeriodRange = (period) => {
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  switch (period) {
    case 'today': {
      const d = startOfDay(now);
      return { startDate: toYMD(d), endDate: toYMD(d) };
    }
    case 'yesterday': {
      const d = startOfDay(now);
      d.setDate(d.getDate() - 1);
      return { startDate: toYMD(d), endDate: toYMD(d) };
    }
    case 'thisWeek': {
      const d = startOfDay(now);
      const diffToMonday = (d.getDay() + 6) % 7;
      const start = new Date(d);
      start.setDate(d.getDate() - diffToMonday);
      return { startDate: toYMD(start), endDate: toYMD(d) };
    }
    case 'thisMonth': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: toYMD(start), endDate: toYMD(startOfDay(now)) };
    }
    case 'lastMonth': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end   = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: toYMD(start), endDate: toYMD(end) };
    }
    default:
      return { startDate: null, endDate: null };
  }
};

// ─── Summary row mapper ──────────────────────────────────────────────────────
// /api/inventory/items/summary returns snake_case rows; map defensively so a
// future camelCase migration of that endpoint doesn't break this page.
const mapSummaryRow = (r) => ({
  itemId:       r.id,
  itemName:     r.name,
  categoryName: r.category      ?? r.categoryName ?? 'N/A',
  unit:         r.unit          ?? '',
  currentStock: r.current_stock ?? r.currentStock ?? 0,
  minimumStock: r.minimum_stock ?? r.minimumStock ?? 0,
  unitCost:     r.unit_cost     ?? r.unitCost     ?? 0,
  stockValue:   r.stock_value   ?? r.stockValue   ?? null,
  stockStatus:  r.stock_status  ?? r.stockStatus  ?? null,
});

const formatDateTime = (iso) => {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }),
  };
};

const stockStatusOf = (item) => {
  if (item.currentStock <= 0) return { label: 'Out of Stock', cls: 'bg-red-100 text-red-700',     icon: PackageX };
  if (item.currentStock <= item.minimumStock)
    return { label: 'Low Stock',    cls: 'bg-amber-100 text-amber-700', icon: AlertTriangle };
  return   { label: 'OK',           cls: 'bg-green-100 text-green-700', icon: CheckCircle2 };
};

// Reference column: purchase → PO Number, consumption (Sale) → Invoice
// Number, wastage → none. NULL references (legacy purchases, manual stock
// updates, bulk uploads) render as '—' — correct, not missing data.
const referenceOf = (m) => {
  switch (m.updateType) {
    case 'purchase':    return m.poNumber   || null;
    case 'consumption': return m.billNumber || null;
    default:            return null;
  }
};

// Windowed page list for the pager: all pages when ≤7; otherwise
// 1 … (current−1, current, current+1) … last, with '…' placeholders.
const getPageNumbers = (current, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages = [1];
  if (current > 3) pages.push('…');
  for (
    let p = Math.max(2, current - 1);
    p <= Math.min(totalPages - 1, current + 1);
    p++
  ) {
    pages.push(p);
  }
  if (current < totalPages - 2) pages.push('…');
  pages.push(totalPages);
  return pages;
};

export default function InventoryItemHistory() {
  const { id } = useParams();
  const navigate = useNavigate();

  // ── Item summary state ──────────────────────────────────────────────────
  // Loaded from the EXISTING summary endpoint (all items), resolved by the
  // numeric :id from the route — never by name matching. Independent of the
  // movements fetch below.
  const [item, setItem] = useState(null);
  const [isLoadingItem, setIsLoadingItem] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const itemId = Number(id);

    if (!Number.isInteger(itemId) || itemId <= 0) {
      toast.error('Invalid inventory item');
      navigate('/inventory/items');
      return;
    }

    (async () => {
      setIsLoadingItem(true);
      try {
        const data = await inventoryitemService.getSummary();
        const rows = Array.isArray(data) ? data : (data?.data ?? []);
        const row = rows.find((r) => Number(r.id) === itemId);
        if (cancelled) return;
        if (!row) {
          toast.error('Inventory item not found');
          navigate('/inventory/items');
          return;
        }
        setItem(mapSummaryRow(row));
      } catch (err) {
        console.error('Failed to load item summary:', err);
        if (!cancelled) toast.error('Failed to load item details');
      } finally {
        if (!cancelled) setIsLoadingItem(false);
      }
    })();

    return () => { cancelled = true; };
  }, [id, navigate]);

  // ── Period filter: draft vs applied (same pattern as Stock Status) ───────
  const initialRange = getPeriodRange('today');
  const [activePreset, setActivePreset] = useState('today');
  const [draftRange, setDraftRange] = useState({
    from: initialRange.startDate,
    to:   initialRange.endDate,
  });
  const [appliedFilter, setAppliedFilter] = useState({
    preset: 'today',
    from:   initialRange.startDate,
    to:     initialRange.endDate,
  });

  const applyPreset = (key) => {
    const { startDate, endDate } = getPeriodRange(key);
    setActivePreset(key);
    setDraftRange({ from: startDate, to: endDate });
    setOffset(0);
    setAppliedFilter({ preset: key, from: startDate, to: endDate });
  };

  const handleDraftChange = (field, value) => {
    setDraftRange((r) => ({ ...r, [field]: value }));
    setActivePreset(null);
  };

  const applyCustomRange = () => {
    if (!draftRange.from || !draftRange.to) return;
    setOffset(0);
    setAppliedFilter({ preset: null, from: draftRange.from, to: draftRange.to });
  };

  const canApplyCustomRange = !!draftRange.from && !!draftRange.to;

  // ── Movement Type filter ────────────────────────────────────────────────
  // Stores the RAW backend type ('purchase' | 'consumption' | 'wastage') or
  // 'all'. Applies immediately on change (same as the Items page filters)
  // and combines with whatever period is currently applied.
  const [selectedType, setSelectedType] = useState('all');

  const handleTypeChange = (value) => {
    setSelectedType(value);
    setOffset(0);
  };

  // ── Movements ───────────────────────────────────────────────────────────
  // Server-side pagination: every page/filter change re-calls the API with
  // offset/limit; `total` from the response drives the pager. 10/page keeps
  // the ledger readable and makes multi-page behaviour visible with
  // realistic data volumes (50 rows = 5 pages).
  const PAGE_SIZE = 10;
  const [offset, setOffset] = useState(0);
  const [isLoadingMovements, setIsLoadingMovements] = useState(false);
  const [movements, setMovements] = useState([]);
  const [total, setTotal] = useState(0);

  // Stale-offset recovery: if the current offset points past the end of the
  // (possibly shrunk) result set — e.g. refreshed while on page 3 after rows
  // were deleted — snap back to the first page instead of stranding the user
  // on an empty page with no pager visible.
  useEffect(() => {
    if (!isLoadingMovements && offset > 0 && total > 0 && offset >= total) {
      setOffset(0);
    }
  }, [isLoadingMovements, offset, total]);

  // Fetches from /api/inventory/item-history whenever the item, period,
  // type, or page changes. Independent of the summary fetch above.
  // Backend contract: preset → period enum; custom range → DATE_RANGE +
  // startDate/endDate; type omitted for 'all' (server treats NULL as all).
  useEffect(() => {
    let cancelled = false;
    const itemId = Number(id);
    if (!Number.isInteger(itemId) || itemId <= 0) return;

    (async () => {
      setIsLoadingMovements(true);
      try {
        const params = appliedFilter.preset
          ? { itemId, period: PERIOD_ENUM[appliedFilter.preset] }
          : {
              itemId,
              period: 'DATE_RANGE',
              startDate: appliedFilter.from,
              endDate: appliedFilter.to,
            };
        if (selectedType !== 'all') params.type = selectedType;
        params.offset = offset;
        params.limit = PAGE_SIZE;

        const res = await inventoryitemService.getItemHistory(params);
        const data = res?.data ?? res ?? {};
        if (cancelled) return;
        setMovements(Array.isArray(data.movements) ? data.movements : []);
        setTotal(Number(data.total) || 0);
      } catch (err) {
        console.error('Failed to load stock movements:', err);
        if (!cancelled) {
          toast.error('Failed to load stock movement history');
          setMovements([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setIsLoadingMovements(false);
      }
    })();

    return () => { cancelled = true; };
  }, [id, appliedFilter, selectedType, offset]);

  const status = useMemo(() => (item ? stockStatusOf(item) : null), [item]);
  const StatusIcon = status?.icon ?? CheckCircle2;
  const unit = item?.unit ?? '';

  return (
    <div className="flex flex-col h-full bg-slate-50 -m-6">

      {/* ── Page header bar ─────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/inventory/items')}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            title="Back to Inventory Items"
          >
            <ArrowLeft size={18} />
          </button>
          <History size={20} className="text-teal-600" />
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-slate-800 leading-tight">Inventory Item History</h1>
            <p className="text-xs text-slate-500">
              Stock movement history for {item?.itemName ?? '…'}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">

        {/* ── 1. Item summary card (old popup content) ─────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4">
          {isLoadingItem ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 size={16} className="animate-spin text-teal-500" />
              Loading item...
            </div>
          ) : !item ? (
            <div className="text-sm text-slate-500">
              Item details could not be loaded.
            </div>
          ) : (
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold text-slate-900">{item.itemName}</h2>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${status?.cls ?? ''}`}>
                    <StatusIcon size={12} />
                    {status?.label}
                  </span>
                </div>
                <p className="text-sm text-slate-500 mt-0.5">{item.categoryName}</p>
              </div>

              <div className="flex flex-wrap gap-x-8 gap-y-2">
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Current Stock</p>
                  <p className="text-sm font-semibold text-slate-900">
                    {Number(item.currentStock).toFixed(2)} {unit}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Min Stock</p>
                  <p className="text-sm font-semibold text-slate-900">
                    {Number(item.minimumStock).toFixed(2)} {unit}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Unit</p>
                  <p className="text-sm font-semibold text-slate-900">{unit}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Unit Cost</p>
                  <p className="text-sm font-semibold text-slate-900">
                    ₹{Number(item.unitCost).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── 2. Filters card ──────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 mb-4 overflow-hidden">
          <div className="p-3 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 flex-wrap">
              {PERIOD_OPTIONS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p.key)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                    activePreset === p.key
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Movement Type filter — same select style as the branch
                selector on the Items page (icon + native select + chevron). */}
            <div className="relative">
              <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <select
                value={selectedType}
                onChange={(e) => handleTypeChange(e.target.value)}
                className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
                title="Filter by movement type"
              >
                {TYPE_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="date"
                value={draftRange.from}
                max={draftRange.to || undefined}
                onChange={(e) => handleDraftChange('from', e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
              <span className="text-slate-400 text-sm">to</span>
              <input
                type="date"
                value={draftRange.to}
                min={draftRange.from || undefined}
                onChange={(e) => handleDraftChange('to', e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
              <button
                onClick={applyCustomRange}
                disabled={!canApplyCustomRange}
                className="px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>
        </div>

        {/* ── 3. Stock Movement History table ─────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2">
              <Boxes size={18} className="text-teal-600" />
              <h2 className="font-semibold text-slate-900">Stock Movement History</h2>
              <span className="text-sm text-slate-500 font-medium">
                {total} movement{total !== 1 ? 's' : ''}
              </span>
              <button
                onClick={() => setAppliedFilter((f) => ({ ...f }))}
                className="ml-auto p-2 text-slate-400 hover:text-teal-600 hover:bg-slate-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw size={16} className={isLoadingMovements ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          {isLoadingMovements ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="text-teal-500 animate-spin" />
              <span className="ml-2 text-sm text-slate-500">Loading movements...</span>
            </div>
          ) : movements.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <History size={24} className="text-slate-500" />
              </div>
              <p className="text-slate-900 font-medium mb-1">No movements</p>
              <p className="text-sm text-slate-500">
                {selectedType === 'all'
                  ? 'No stock movements were recorded for this item in the selected period.'
                  : `No ${TYPE_OPTIONS.find((t) => t.value === selectedType)?.label.toLowerCase()} movements were recorded for this item in the selected period.`}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="w-[15%] px-4 py-3 text-left  text-xs font-semibold text-slate-600 uppercase">Date &amp; Time</th>
                    <th className="w-[10%] px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Type</th>
                    <th className="w-[13%] px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Quantity</th>
                    <th className="w-[13%] px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Stock Before</th>
                    <th className="w-[13%] px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Stock After</th>
                    <th className="w-[12%] px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Unit Cost</th>
                    {/* Centered (like Type): with a right-aligned column on
                        its left, centering makes the gap come from the
                        column's own width — symmetric whitespace on both
                        sides, visually consistent with the rest of the
                        table. 24% comfortably fits INV/2025-26-00016. */}
                    <th className="w-[24%] px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Reference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {movements.map((m) => {
                    const cfg = TYPE_CONFIG[m.updateType] ?? {
                      label: m.updateType, badge: 'bg-slate-100 text-slate-600',
                      qty: 'text-slate-600', sign: '',
                    };
                    const dt = formatDateTime(m.createdAt);
                    return (
                      <tr key={m.id} className="hover:bg-slate-50 transition-colors">
                        {/* Date & Time — leads the ledger; the page is
                            single-item so the old Item column was removed. */}
                        <td className="px-4 py-3">
                          <div className="flex flex-col items-start">
                            <span className="text-sm text-slate-900">{dt.date}</span>
                            <span className="text-xs text-slate-400">{dt.time}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.badge}`}>
                            {cfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {/* Sign derives from updateType, value from ABS(quantity) —
                              never trust the stored sign convention directly. */}
                          <span className={`text-sm font-medium ${cfg.qty}`}>
                            {cfg.sign}{Math.abs(Number(m.quantity)).toFixed(2)} {unit}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm text-slate-600">
                            {m.stockBefore != null ? `${Number(m.stockBefore).toFixed(2)} ${unit}` : '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-semibold text-slate-900">
                            {m.stockAfter != null ? `${Number(m.stockAfter).toFixed(2)} ${unit}` : '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm text-slate-600">
                            {m.unitCost != null ? `₹${Number(m.unitCost).toFixed(2)}` : '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {/* purchase → PO Number, consumption → Invoice
                              Number, wastage → '—'. */}
                          {referenceOf(m) ? (
                            <span
                              className="text-sm font-medium text-slate-700 truncate inline-block max-w-full align-middle"
                              title={referenceOf(m)}
                            >
                              {referenceOf(m)}
                            </span>
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!isLoadingMovements && movements.length > 0 && (() => {
            const totalPages  = Math.max(1, Math.ceil(total / PAGE_SIZE));
            const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
            return (
              <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs text-slate-500">
                  Showing {offset + 1}–{offset + movements.length} of {total} movements
                </span>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {getPageNumbers(currentPage, totalPages).map((p, i) =>
                      p === '…' ? (
                        <span key={`e-${i}`} className="px-1.5 text-xs text-slate-400 select-none">
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setOffset((p - 1) * PAGE_SIZE)}
                          className={`min-w-[32px] px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                            p === currentPage
                              ? 'bg-teal-600 text-white'
                              : 'text-slate-600 bg-white border border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {p}
                        </button>
                      )
                    )}
                    <button
                      onClick={() => setOffset((o) => o + PAGE_SIZE)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}